import React, { useState } from 'react';
import { Edit2, Trash2, Plus, GripVertical, Save, X, Lock, Eye, EyeOff } from 'lucide-react';
import '../styles/link-manager.css';

const LinkManager = ({ links, onUpdate }) => {
  const [editingId, setEditingId] = useState(null);
  const [addingNew, setAddingNew] = useState(false);
  const [formData, setFormData] = useState({});
  const [draggedItem, setDraggedItem] = useState(null);
  const [dragOverItem, setDragOverItem] = useState(null);

  const iconOptions = [
    'Film', 'Users', 'Building2', 'FileText', 'ExternalLink',
    'Globe', 'Mail', 'Phone', 'MessageSquare', 'Download'
  ];

  const startEdit = (link) => {
    setEditingId(link.id);
    setFormData({
      title: link.title,
      url: link.url,
      icon: link.icon,
      color_theme: link.color_theme || 'blue',
      has_gate: link.has_gate || false,
      gate_resource_name: link.gate_resource_name || '',
      gate_fields: link.gate_fields || {
        name: { enabled: true, required: true },
        email: { enabled: true, required: true },
        phone: { enabled: false, required: false },
        linkedin: { enabled: false, required: false }
      },
      enabled: link.enabled !== false
    });
  };

  const startAdd = () => {
    setAddingNew(true);
    setFormData({
      title: '',
      url: '',
      icon: 'ExternalLink',
      color_theme: 'blue',
      has_gate: false,
      gate_resource_name: '',
      gate_fields: {
        name: { enabled: true, required: true },
        email: { enabled: true, required: true },
        phone: { enabled: false, required: false },
        linkedin: { enabled: false, required: false }
      },
      enabled: true
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setAddingNew(false);
    setFormData({});
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleGateFieldChange = (fieldName, property, value) => {
    setFormData(prev => ({
      ...prev,
      gate_fields: {
        ...prev.gate_fields,
        [fieldName]: {
          ...prev.gate_fields[fieldName],
          [property]: value
        }
      }
    }));
  };

  const handleSave = async () => {
    // Validate that title and URL are not empty
    if (!formData.title || !formData.title.trim()) {
      alert('Please enter a title for the link');
      return;
    }
    
    if (!formData.url || !formData.url.trim()) {
      alert('Please enter a URL for the link');
      return;
    }
    
    if (addingNew) {
      await onUpdate('create', null, formData);
    } else if (editingId) {
      await onUpdate('update', editingId, formData);
    }
    cancelEdit();
  };

  const handleDelete = async (linkId) => {
    if (window.confirm('Are you sure you want to delete this link?')) {
      try {
        await onUpdate('delete', linkId);
        console.log('Link deleted successfully');
      } catch (error) {
        console.error('Failed to delete link:', error);
        alert('Failed to delete link. Please try again.');
      }
    }
  };

  const handleToggleEnabled = async (link) => {
    await onUpdate('update', link.id, { enabled: !link.enabled });
  };

  // Drag and drop handlers
  const handleDragStart = (e, link) => {
    setDraggedItem(link);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, link) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    
    if (draggedItem && link.id !== draggedItem.id) {
      setDragOverItem(link);
    }
  };

  const handleDragLeave = () => {
    setDragOverItem(null);
  };

  const handleDrop = async (e, targetLink) => {
    e.preventDefault();
    
    if (!draggedItem || draggedItem.id === targetLink.id) {
      setDraggedItem(null);
      setDragOverItem(null);
      return;
    }

    // Reorder the links
    const reorderedLinks = [...links];
    const draggedIndex = reorderedLinks.findIndex(l => l.id === draggedItem.id);
    const targetIndex = reorderedLinks.findIndex(l => l.id === targetLink.id);

    // Remove dragged item and insert at new position
    const [removed] = reorderedLinks.splice(draggedIndex, 1);
    reorderedLinks.splice(targetIndex, 0, removed);

    // Get array of link IDs in new order
    const linkIds = reorderedLinks.map(l => l.id);
    
    // Call parent's onUpdate with reorder action
    await onUpdate('reorder', null, linkIds);

    setDraggedItem(null);
    setDragOverItem(null);
  };

  const handleDragEnd = () => {
    setDraggedItem(null);
    setDragOverItem(null);
  };

  // Touch handlers for mobile
  const handleTouchStart = (e, link) => {
    setDraggedItem(link);
  };

  const handleTouchMove = (e) => {
    if (!draggedItem) return;
    
    e.preventDefault();
    const touch = e.touches[0];
    const element = document.elementFromPoint(touch.clientX, touch.clientY);
    
    // Find the link item being touched
    const linkItem = element?.closest('.link-item');
    if (linkItem) {
      const linkId = linkItem.getAttribute('data-link-id');
      const targetLink = links.find(l => l.id === linkId);
      
      if (targetLink && targetLink.id !== draggedItem.id) {
        setDragOverItem(targetLink);
      }
    }
  };

  const handleTouchEnd = async (e, targetLink) => {
    if (!draggedItem || !dragOverItem) {
      setDraggedItem(null);
      setDragOverItem(null);
      return;
    }

    // Use dragOverItem as target since touch doesn't have dataTransfer
    const actualTarget = dragOverItem;

    if (draggedItem.id === actualTarget.id) {
      setDraggedItem(null);
      setDragOverItem(null);
      return;
    }

    // Reorder the links
    const reorderedLinks = [...links];
    const draggedIndex = reorderedLinks.findIndex(l => l.id === draggedItem.id);
    const targetIndex = reorderedLinks.findIndex(l => l.id === actualTarget.id);

    // Remove dragged item and insert at new position
    const [removed] = reorderedLinks.splice(draggedIndex, 1);
    reorderedLinks.splice(targetIndex, 0, removed);

    // Get array of link IDs in new order
    const linkIds = reorderedLinks.map(l => l.id);
    
    // Call parent's onUpdate with reorder action
    await onUpdate('reorder', null, linkIds);

    setDraggedItem(null);
    setDragOverItem(null);
  };

  return (
    <div className="link-manager">
      <div className="link-manager-header">
        <h3>Manage Links</h3>
        <button onClick={startAdd} className="add-link-btn" disabled={addingNew}>
          <Plus size={18} />
          Add Link
        </button>
      </div>

      <div className="links-list">
        {addingNew && (
          <div className="link-form">
            <div className="form-row">
              <div className="form-group">
                <label>Title</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => handleChange('title', e.target.value)}
                  placeholder="Link title"
                />
              </div>
              <div className="form-group">
                <label>URL</label>
                <input
                  type="url"
                  value={formData.url}
                  onChange={(e) => handleChange('url', e.target.value)}
                  placeholder="https://example.com"
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Icon</label>
                <select
                  value={formData.icon}
                  onChange={(e) => handleChange('icon', e.target.value)}
                >
                  {iconOptions.map(icon => (
                    <option key={icon} value={icon}>{icon}</option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Color Theme</label>
                <select
                  value={formData.color_theme || 'blue'}
                  onChange={(e) => handleChange('color_theme', e.target.value)}
                  className="color-theme-select"
                >
                  <option value="blue">Blue (Default)</option>
                  <option value="green">Green</option>
                  <option value="red">Red</option>
                  <option value="purple">Purple</option>
                  <option value="orange">Orange</option>
                  <option value="teal">Teal</option>
                  <option value="indigo">Indigo</option>
                  <option value="pink">Pink</option>
                </select>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group checkbox-group">
                <label>
                  <input
                    type="checkbox"
                    checked={formData.has_gate}
                    onChange={(e) => handleChange('has_gate', e.target.checked)}
                  />
                  <span>Require Email Gate</span>
                </label>
              </div>
            </div>

            {formData.has_gate && (
              <>
                <div className="form-row">
                  <div className="form-group">
                    <label>Gate Resource Name</label>
                    <input
                      type="text"
                      value={formData.gate_resource_name}
                      onChange={(e) => handleChange('gate_resource_name', e.target.value)}
                      placeholder="e.g., Special Report"
                    />
                  </div>
                </div>

                <div className="gate-fields-config">
                  <h4>Configure Fields to Collect</h4>
                  <p className="config-description">Select which information to collect from users accessing this resource</p>
                  
                  <div className="field-config-grid">
                    {/* Name Field */}
                    <div className="field-config-row">
                      <div className="field-checkbox">
                        <input
                          type="checkbox"
                          id={`field-name-${editingId || 'new'}`}
                          checked={formData.gate_fields?.name?.enabled || false}
                          onChange={(e) => handleGateFieldChange('name', 'enabled', e.target.checked)}
                        />
                        <label htmlFor={`field-name-${editingId || 'new'}`}>Name</label>
                      </div>
                      {formData.gate_fields?.name?.enabled && (
                        <div className="field-required">
                          <input
                            type="checkbox"
                            id={`field-name-required-${editingId || 'new'}`}
                            checked={formData.gate_fields?.name?.required || false}
                            onChange={(e) => handleGateFieldChange('name', 'required', e.target.checked)}
                          />
                          <label htmlFor={`field-name-required-${editingId || 'new'}`}>Required</label>
                        </div>
                      )}
                    </div>

                    {/* Email Field */}
                    <div className="field-config-row">
                      <div className="field-checkbox">
                        <input
                          type="checkbox"
                          id={`field-email-${editingId || 'new'}`}
                          checked={formData.gate_fields?.email?.enabled || false}
                          onChange={(e) => handleGateFieldChange('email', 'enabled', e.target.checked)}
                        />
                        <label htmlFor={`field-email-${editingId || 'new'}`}>Email</label>
                      </div>
                      {formData.gate_fields?.email?.enabled && (
                        <div className="field-required">
                          <input
                            type="checkbox"
                            id={`field-email-required-${editingId || 'new'}`}
                            checked={formData.gate_fields?.email?.required || false}
                            onChange={(e) => handleGateFieldChange('email', 'required', e.target.checked)}
                          />
                          <label htmlFor={`field-email-required-${editingId || 'new'}`}>Required</label>
                        </div>
                      )}
                    </div>

                    {/* Phone Field */}
                    <div className="field-config-row">
                      <div className="field-checkbox">
                        <input
                          type="checkbox"
                          id={`field-phone-${editingId || 'new'}`}
                          checked={formData.gate_fields?.phone?.enabled || false}
                          onChange={(e) => handleGateFieldChange('phone', 'enabled', e.target.checked)}
                        />
                        <label htmlFor={`field-phone-${editingId || 'new'}`}>Phone Number</label>
                      </div>
                      {formData.gate_fields?.phone?.enabled && (
                        <div className="field-required">
                          <input
                            type="checkbox"
                            id={`field-phone-required-${editingId || 'new'}`}
                            checked={formData.gate_fields?.phone?.required || false}
                            onChange={(e) => handleGateFieldChange('phone', 'required', e.target.checked)}
                          />
                          <label htmlFor={`field-phone-required-${editingId || 'new'}`}>Required</label>
                        </div>
                      )}
                    </div>

                    {/* LinkedIn Field */}
                    <div className="field-config-row">
                      <div className="field-checkbox">
                        <input
                          type="checkbox"
                          id={`field-linkedin-${editingId || 'new'}`}
                          checked={formData.gate_fields?.linkedin?.enabled || false}
                          onChange={(e) => handleGateFieldChange('linkedin', 'enabled', e.target.checked)}
                        />
                        <label htmlFor={`field-linkedin-${editingId || 'new'}`}>LinkedIn Profile</label>
                      </div>
                      {formData.gate_fields?.linkedin?.enabled && (
                        <div className="field-required">
                          <input
                            type="checkbox"
                            id={`field-linkedin-required-${editingId || 'new'}`}
                            checked={formData.gate_fields?.linkedin?.required || false}
                            onChange={(e) => handleGateFieldChange('linkedin', 'required', e.target.checked)}
                          />
                          <label htmlFor={`field-linkedin-required-${editingId || 'new'}`}>Required</label>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </>
            )}

            <div className="form-actions">
              <button onClick={handleSave} className="save-btn">
                <Save size={16} />
                Save
              </button>
              <button onClick={cancelEdit} className="cancel-btn">
                <X size={16} />
                Cancel
              </button>
            </div>
          </div>
        )}

        {links.map(link => (
          <div 
            key={link.id}
            data-link-id={link.id}
            className={`link-item ${!link.enabled ? 'disabled' : ''} ${dragOverItem?.id === link.id ? 'drag-over' : ''} ${draggedItem?.id === link.id ? 'dragging' : ''}`}
            draggable={editingId !== link.id}
            onDragStart={(e) => handleDragStart(e, link)}
            onDragOver={(e) => handleDragOver(e, link)}
            onDragLeave={handleDragLeave}
            onDrop={(e) => handleDrop(e, link)}
            onDragEnd={handleDragEnd}
            onTouchStart={(e) => handleTouchStart(e, link)}
            onTouchMove={handleTouchMove}
            onTouchEnd={(e) => handleTouchEnd(e, link)}
          >
            {editingId === link.id ? (
              <div className="link-form">
                <div className="form-row">
                  <div className="form-group">
                    <label>Title</label>
                    <input
                      type="text"
                      value={formData.title}
                      onChange={(e) => handleChange('title', e.target.value)}
                    />
                  </div>
                  <div className="form-group">
                    <label>URL</label>
                    <input
                      type="url"
                      value={formData.url}
                      onChange={(e) => handleChange('url', e.target.value)}
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Icon</label>
                    <select
                      value={formData.icon}
                      onChange={(e) => handleChange('icon', e.target.value)}
                    >
                      {iconOptions.map(icon => (
                        <option key={icon} value={icon}>{icon}</option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Color Theme</label>
                    <select
                      value={formData.color_theme || 'blue'}
                      onChange={(e) => handleChange('color_theme', e.target.value)}
                      className="color-theme-select"
                    >
                      <option value="blue">Blue (Default)</option>
                      <option value="green">Green</option>
                      <option value="red">Red</option>
                      <option value="purple">Purple</option>
                      <option value="orange">Orange</option>
                      <option value="teal">Teal</option>
                      <option value="indigo">Indigo</option>
                      <option value="pink">Pink</option>
                    </select>
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group checkbox-group">
                    <label>
                      <input
                        type="checkbox"
                        checked={formData.has_gate}
                        onChange={(e) => handleChange('has_gate', e.target.checked)}
                      />
                      <span>Require Email Gate</span>
                    </label>
                  </div>
                </div>

                {formData.has_gate && (
                  <>
                    <div className="form-row">
                      <div className="form-group">
                        <label>Gate Resource Name</label>
                        <input
                          type="text"
                          value={formData.gate_resource_name}
                          onChange={(e) => handleChange('gate_resource_name', e.target.value)}
                        />
                      </div>
                    </div>

                    <div className="gate-fields-config">
                      <h4>Configure Fields to Collect</h4>
                      <p className="config-description">Select which information to collect from users accessing this resource</p>
                      
                      <div className="field-config-grid">
                        {/* Name Field */}
                        <div className="field-config-row">
                          <div className="field-checkbox">
                            <input
                              type="checkbox"
                              id={`field-name-${editingId || 'new'}`}
                              checked={formData.gate_fields?.name?.enabled || false}
                              onChange={(e) => handleGateFieldChange('name', 'enabled', e.target.checked)}
                            />
                            <label htmlFor={`field-name-${editingId || 'new'}`}>Name</label>
                          </div>
                          {formData.gate_fields?.name?.enabled && (
                            <div className="field-required">
                              <input
                                type="checkbox"
                                id={`field-name-required-${editingId || 'new'}`}
                                checked={formData.gate_fields?.name?.required || false}
                                onChange={(e) => handleGateFieldChange('name', 'required', e.target.checked)}
                              />
                              <label htmlFor={`field-name-required-${editingId || 'new'}`}>Required</label>
                            </div>
                          )}
                        </div>

                        {/* Email Field */}
                        <div className="field-config-row">
                          <div className="field-checkbox">
                            <input
                              type="checkbox"
                              id={`field-email-${editingId || 'new'}`}
                              checked={formData.gate_fields?.email?.enabled || false}
                              onChange={(e) => handleGateFieldChange('email', 'enabled', e.target.checked)}
                            />
                            <label htmlFor={`field-email-${editingId || 'new'}`}>Email</label>
                          </div>
                          {formData.gate_fields?.email?.enabled && (
                            <div className="field-required">
                              <input
                                type="checkbox"
                                id={`field-email-required-${editingId || 'new'}`}
                                checked={formData.gate_fields?.email?.required || false}
                                onChange={(e) => handleGateFieldChange('email', 'required', e.target.checked)}
                              />
                              <label htmlFor={`field-email-required-${editingId || 'new'}`}>Required</label>
                            </div>
                          )}
                        </div>

                        {/* Phone Field */}
                        <div className="field-config-row">
                          <div className="field-checkbox">
                            <input
                              type="checkbox"
                              id={`field-phone-${editingId || 'new'}`}
                              checked={formData.gate_fields?.phone?.enabled || false}
                              onChange={(e) => handleGateFieldChange('phone', 'enabled', e.target.checked)}
                            />
                            <label htmlFor={`field-phone-${editingId || 'new'}`}>Phone Number</label>
                          </div>
                          {formData.gate_fields?.phone?.enabled && (
                            <div className="field-required">
                              <input
                                type="checkbox"
                                id={`field-phone-required-${editingId || 'new'}`}
                                checked={formData.gate_fields?.phone?.required || false}
                                onChange={(e) => handleGateFieldChange('phone', 'required', e.target.checked)}
                              />
                              <label htmlFor={`field-phone-required-${editingId || 'new'}`}>Required</label>
                            </div>
                          )}
                        </div>

                        {/* LinkedIn Field */}
                        <div className="field-config-row">
                          <div className="field-checkbox">
                            <input
                              type="checkbox"
                              id={`field-linkedin-${editingId || 'new'}`}
                              checked={formData.gate_fields?.linkedin?.enabled || false}
                              onChange={(e) => handleGateFieldChange('linkedin', 'enabled', e.target.checked)}
                            />
                            <label htmlFor={`field-linkedin-${editingId || 'new'}`}>LinkedIn Profile</label>
                          </div>
                          {formData.gate_fields?.linkedin?.enabled && (
                            <div className="field-required">
                              <input
                                type="checkbox"
                                id={`field-linkedin-required-${editingId || 'new'}`}
                                checked={formData.gate_fields?.linkedin?.required || false}
                                onChange={(e) => handleGateFieldChange('linkedin', 'required', e.target.checked)}
                              />
                              <label htmlFor={`field-linkedin-required-${editingId || 'new'}`}>Required</label>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </>
                )}

                <div className="form-actions">
                  <button onClick={handleSave} className="save-btn">
                    <Save size={16} />
                    Save
                  </button>
                  <button onClick={cancelEdit} className="cancel-btn">
                    <X size={16} />
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <>
                <div className="link-info">
                  <div className="link-title">
                    {link.title}
                    {link.has_gate && (
                      <span className="gate-badge" title="Email gate enabled - Edit link to disable">
                        <Lock size={14} />
                      </span>
                    )}
                  </div>
                  <div className="link-url">{link.url}</div>
                  {link.has_gate && link.gate_resource_name && (
                    <div className="link-gate-info">
                      <Lock size={12} /> Gated: {link.gate_resource_name} (edit to remove gate)
                    </div>
                  )}
                </div>
                <div className="link-actions">
                  <div className="link-grip">
                    <GripVertical size={20} />
                  </div>
                  <button
                    onClick={() => handleToggleEnabled(link)}
                    className={`toggle-btn ${link.enabled ? 'enabled' : 'disabled'}`}
                    title={link.enabled ? 'Click to hide this link from main page' : 'Click to show this link on main page'}
                  >
                    {link.enabled ? <Eye size={18} /> : <EyeOff size={18} />}
                  </button>
                  <button onClick={() => startEdit(link)} className="edit-btn" title="Edit link">
                    <Edit2 size={18} />
                  </button>
                  <button onClick={() => handleDelete(link.id)} className="delete-btn" title="Delete link">
                    <Trash2 size={18} />
                  </button>
                </div>
              </>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default LinkManager;
